import { useEffect, useRef, useState } from 'react'
import { GOOGLE_AD_MANAGER_PLACEMENTS } from './adPlacements.js'
import { resolveFittingAdVariantMode } from './adVariantSelection.js'
import { useMobileViewport } from './useAdsterraLoadCycle.js'

const FORMATS = {
  '728x90': 'Leaderboard',
  '468x60': 'Banner',
  '320x50': 'Banner móvel',
  '300x50': 'Banner compacto',
  '300x250': 'Retângulo médio',
  '300x600': 'Meia página',
}

// Pure local preview: no provider component, request, or ad telemetry.
export default function MockAdSlot({ placement, hidden = false, hideOnMobile = false, className = '', testId = 'ad-waterfall-slot', slotId, label = 'Publicidade', onCreativeStatusChange, onProviderChange, observeRef, config: customConfig, position, experiment = 'baseline', experimentVariant = 'control' }) {
  const shell = useRef(null)
  const config = customConfig || GOOGLE_AD_MANAGER_PLACEMENTS[placement]
  const mobile = useMobileViewport()
  const disabled = hidden || !config || (hideOnMobile && mobile)
  const measure = observeRef || shell
  const [width, setWidth] = useState(0)

  useEffect(() => {
    if (disabled) return undefined
    const node = measure.current
    if (!node) return undefined
    const read = entry => {
      const padding = getComputedStyle(node)
      const measured = entry?.contentRect?.width ?? (node.clientWidth - (parseFloat(padding.paddingLeft) || 0) - (parseFloat(padding.paddingRight) || 0))
      setWidth(Math.floor(measured || node.getBoundingClientRect().width || 0))
    }
    read()
    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(entries => read(entries[0]))
      observer.observe(node)
      return () => observer.disconnect()
    }
    const onResize = () => read()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [disabled, measure])

  // Mocks may adapt freely to resizing: no real impression is refreshed.
  const mode = disabled ? null : resolveFittingAdVariantMode({
    variants: config.variants,
    preferredMode: config.preferredMode || (mobile ? 'mobile' : 'desktop'),
    availableWidth: width,
    allowUnconfigured: true,
  })
  const variant = config?.variants[mode]
  const status = variant ? 'filled' : width > 0 ? 'empty' : 'loading'
  useEffect(() => { if (!disabled) onCreativeStatusChange?.(status) }, [disabled, status, onCreativeStatusChange])
  useEffect(() => { if (!disabled) onProviderChange?.('mock') }, [disabled, onProviderChange])

  if (disabled) return null
  const configured = variant?.configured ?? Boolean(variant?.adUnitPath)
  const unitStatus = configured ? 'configured' : 'pending'
  const legend = configured ? 'Unidade configurada' : 'Unidade pendente'
  const format = variant && (FORMATS[`${variant.width}x${variant.height}`] || 'Display')
  const colors = configured
    ? 'border-emerald-400 bg-emerald-950/80 text-emerald-100'
    : 'border-amber-400 bg-amber-950/80 text-amber-100'

  return <div ref={shell} className={`flex justify-center ${className}`} data-ad-creative-status={status} data-ad-placement={placement} data-ad-slot-id={slotId} data-ad-position={position} data-ad-experiment={experiment} data-ad-experiment-variant={experimentVariant} data-ad-provider="mock" data-ad-variant-mode={mode || undefined} data-testid={testId}>
    {variant && <div role="img" aria-label={`${label}: anúncio simulado, ${legend.toLowerCase()}, ${format}, ${variant.width} por ${variant.height} pixels, ${placement}`} title={configured ? `Unidade configurada: ${variant.adUnitPath}\nPosição: ${slotId || placement}` : `Nenhuma unidade Google ativa para este formato.\nPosição: ${slotId || placement}`} data-ad-mock="true" data-ad-unit-status={unitStatus} data-testid={`${testId}-mock`} className={`flex flex-col items-center justify-center border-2 border-dashed px-2 text-center ${colors}`} style={{ width: variant.width, height: variant.height, boxSizing: 'border-box', flexShrink: 0 }}>
      <span className="text-[9px] font-semibold leading-tight">Simulado · {legend}</span>
      <span className="font-mono text-[13px] font-semibold leading-tight">{variant.width} × {variant.height} px · {format}</span>
      <span className="max-w-full truncate font-mono text-[10px] leading-tight">{placement}{position ? ` · ${position}` : ''}{experiment !== 'baseline' ? ' · Experimento' : ''}</span>
    </div>}
  </div>
}
